/**
 * Utility functions for persona summary HTML generation and URL management
 * Centralizes the logic to avoid duplication across the codebase
 */

/**
 * Generates the HTML structure for a persona summary
 * @param {Object} persona - The persona object with _id and summary
 * @returns {string} The HTML string for the persona summary
 */
export const generatePersonaSummaryHTML = (persona) => {
  if (!persona || !persona._id || !persona.summary) {
    return "";
  }

  return `<p><span data-type="persona-summary" data-persona-id="${persona._id}" data-summary="${persona.summary}" class="persona-summary"><span class="persona-summary-text">${persona.summary}</span></span></p>`;
};

/**
 * Updates the browser URL with the persona summary
 * @param {string} searchText - The search text HTML to encode in the URL
 * @returns {Promise<void>}
 */
export const updateURLWithPersonaSummary = async (searchText) => {
  if (typeof window === 'undefined' || !window.history) {
    return;
  }

  const { encodeSearchText } = await import("./urlEncoder.js");
  const encodedQuery = encodeSearchText(searchText);
  const newUrl = searchText ? `/search?query=${encodedQuery}` : '/search';
  window.history.pushState({}, '', newUrl);
};

/**
 * Updates both searchText state and URL with persona summary
 * @param {Object} persona - The persona object
 * @param {Function} setSearchText - The searchText setter function
 * @returns {Promise<string>} The generated HTML
 */
export const updateSearchTextAndURL = async (persona, setSearchText) => {
  const newSearchText = generatePersonaSummaryHTML(persona);
  
  if (setSearchText) {
    setSearchText(newSearchText);
    localStorage.setItem("searchText", newSearchText);
  }
  
  await updateURLWithPersonaSummary(newSearchText);
  
  return newSearchText;
};
